import { useCallback, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';
import { debounce } from 'lodash';
import { media } from 'ui';
import { impersonateUser as impersonateUserAction, logout as logoutAction } from 'actions/user';
import { gql } from 'apollo-boost';
import { useLazyQuery } from '@apollo/react-hooks';
import { Avatar, Box, Button, Legend, List, Name, Stack, capitalize } from '@tymate/sergic';
import { useTranslation } from 'react-i18next';
import { MdSearch } from 'react-icons/md';
import { Scrollbars } from 'react-custom-scrollbars';
import tinycolor from 'tinycolor2';
import { Logo } from 'containers/Auth';
import { SERGIC_OFFER } from '../constants';
import { Spinner } from '@tymate/margaret';
import { history } from '../store';

const Wrapper = styled(Box)`
   background-color: ${({ theme }) => theme.background};
   min-height: 100vh;
   padding: ${({ theme }) => theme.spacing()};

   ${media.tablet`
    padding: ${({ theme }) => theme.spacing(2)}
  `}
`;

const SearchBox = styled(Stack)`
   background-color: #fff;
   width: 800px;
   max-width: 100%;
   border-radius: ${({ theme }) => theme.borderRadius};
   box-shadow: 0 4px 8px rgba(0, 0, 0, 0.12);

   &:focus,
   &:active {
      border: 1px solid ${({ theme }) => theme.primary};
   }
`;

const ALL_IDENTITIES = gql`
   query allIdentities($offer: PlaceSergicOffers, $after: String, $order: Order, $search: String) {
      allIdentities(
         byOffer: $offer
         coownerOnly: true
         registeredOnly: true
         after: $after
         first: 20
         order: $order
         search: $search
      ) {
         pageInfo {
            endCursor
            hasNextPage
         }
         edges {
            node {
               id
               sqlId
               displayName
               email
               sergicOffers
               avatar {
                  fileUrl
               }
               user {
                  sqlId
               }
            }
         }
      }
   }
`;

const SearchWrapper = styled(Box)`
   position: relative;
   width: 100%;
`;

const SearchInput = styled.input`
   appearance: none;
   padding-left: ${({ theme }) => theme.spacing(2)};
   border: 0;
   outline: none;
   display: block;
   width: 100%;
   font-size: 18px;
   height: 42px;

   ${media.tablet`
    height: 50px;
    font-size: 20px;
  `};
`;

const SearchIcon = styled.div`
   position: absolute;
   top: 50%;
   transform: translateY(-50%);
`;

const ResultButton = styled(Stack)`
   border-radius: ${({ theme }) => theme.borderRadius};
   border: 0;
   cursor: pointer;
   background: transparent;

   &:hover {
      background-color: ${({ theme }) =>
         tinycolor(theme.primary)
            .setAlpha(0.06)
            .toString()};
   }
`;

const LogoWrapper = styled.div`
   img {
      max-width: 120px;
      margin-bottom: 0 !important;
   }
`;

const Search = ({ onChange }) => {
   const [search, setSearch] = useState('');

   const handleKeyDown = event => {
      if (event.key === 'Enter' && search?.length >= 3) {
         onChange(search);
      }
   };

   return (
      <SearchWrapper paddingVertical={0.5} paddingHorizontal={1}>
         <SearchIcon>
            <MdSearch size={28} />
         </SearchIcon>

         <SearchInput
            placeholder='Rechercher un contact à incarner'
            value={search}
            onChange={e => setSearch(e.target.value)}
            onKeyDown={e => handleKeyDown(e)}
         />
      </SearchWrapper>
   );
};

const Impersonate = () => {
   const dispatch = useDispatch();
   const { t } = useTranslation();
   const [shouldShowResults, setShouldShowResults] = useState();
   const contactsScrollRef = useRef();

   const logout = useCallback(() => dispatch(logoutAction()), [dispatch]);
   const impersonateUser = useCallback(id => dispatch(impersonateUserAction(id)), [dispatch]);

   const [fetch, { data, fetchMore, loading }] = useLazyQuery(ALL_IDENTITIES);

   const handleLoadMore = debounce(() => {
      fetchMore({
         variables: {
            after: data?.allIdentities?.pageInfo?.endCursor,
            offer: SERGIC_OFFER,
         },
         updateQuery: (prev, { fetchMoreResult }) => {
            let newResult = {
               ...prev,
               allIdentities: {
                  ...prev.allIdentities,
                  pageInfo: fetchMoreResult?.allIdentities?.pageInfo || {},
                  edges: [...prev.allIdentities.edges, ...(fetchMoreResult?.allIdentities?.edges || [])],
               },
            };

            return newResult;
         },
      });
   }, 200);

   return (
      <Wrapper>
         <Stack direction='column' gutterSize={2} alignX='center'>
            <Stack alignX='space-between' alignY='center' size='full'>
               <LogoWrapper>
                  <Logo />
               </LogoWrapper>

               <Button onClick={logout} variant='primary' size='small'>
                  {t('header:logout')}
               </Button>
            </Stack>

            <SearchBox direction='column'>
               <Search
                  onChange={search => {
                     fetch({ variables: { search, offer: SERGIC_OFFER } });
                     setShouldShowResults(Boolean(search));
                  }}
               />

               {Boolean(shouldShowResults) && (data?.allIdentities?.edges || []).length > 0 && (
                  <Scrollbars
                     autoHeight
                     autoHeightMax='calc(100vh - 190px)'
                     ref={contactsScrollRef}
                     onScroll={() => {
                        const scrollRef = contactsScrollRef.current;
                        if (
                           !scrollRef ||
                           loading ||
                           !scrollRef.getValues() ||
                           scrollRef.getValues().top < 0.9 ||
                           !Boolean(data?.allIdentities?.pageInfo?.hasNextPage)
                        ) {
                           return;
                        }

                        handleLoadMore();
                     }}
                  >
                     <List direction='column' alignX='stretch'>
                        {data.allIdentities.edges
                           .map(({ node }) => ({ ...node }))
                           .map(contact => (
                              <Box as='li' key={contact.id}>
                                 <ResultButton
                                    gutterSize={0.5}
                                    as='button'
                                    onClick={() => {
                                       impersonateUser(contact?.user?.sqlId);
                                       history.push('/');
                                    }}
                                    paddingVertical={0.5}
                                    paddingHorizontal={0.5}
                                    size='full'
                                    alignY='center'
                                 >
                                    <Avatar
                                       size='small'
                                       displayName={contact.displayName}
                                       avatarURL={contact.avatar?.fileUrl}
                                    />
                                    <Stack direction='column'>
                                       <Name>{capitalize(contact.displayName)}</Name>
                                       <Legend>{contact.email}</Legend>
                                    </Stack>
                                 </ResultButton>
                              </Box>
                           ))}
                     </List>
                  </Scrollbars>
               )}
            </SearchBox>

            {Boolean(loading) && <Spinner />}
         </Stack>
      </Wrapper>
   );
};

export default Impersonate;
